/**
 * BatchTrackExporter - Modal for exporting/importing multiple tracks
 * Supports full audio data export/import via Base64 encoding
 * Audio formats: WAV (lossless), MP3 (compressed), OGG Vorbis (best compression)
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import lamejs from 'lamejs';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Progress } from '../../ui/progress';
import {
  X,
  Download,
  Upload,
  Check,
  CheckSquare,
  Square,
  Music,
  Sliders,
  FileJson,
  Package,
  AlertCircle,
  Loader2,
  FileDown,
  FileUp,
  HardDrive,
  Waves,
  Info,
  Zap,
  Settings2,
  FileAudio,
} from 'lucide-react';

// Track type icons
const TRACK_ICONS = {
  audio: Music,
  instrument: Music,
  drums: Sliders,
  default: Music,
};

// Track type colors
const TRACK_COLORS = {
  audio: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  instrument: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  drums: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  default: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
};

// Audio format options
const AUDIO_FORMATS = {
  wav: {
    label: 'WAV (Lossless)',
    icon: Waves,
    color: 'text-cyan-400',
    description: 'Uncompressed, largest files',
    ratio: 1.0,
  },
  mp3: {
    label: 'MP3',
    icon: Zap,
    color: 'text-emerald-400',
    description: 'Good compression, wide compatibility',
    ratio: 0.10, // Base ratio, adjusted by quality
  },
  ogg: {
    label: 'OGG Vorbis',
    icon: FileAudio,
    color: 'text-violet-400',
    description: 'Best compression, smaller files',
    ratio: 0.08, // Base ratio, adjusted by quality
  },
};

// Compression quality presets
const COMPRESSION_QUALITY = {
  low: { 
    bitrate: 96, 
    label: 'Low (96 kbps)', 
    mp3Ratio: 0.06, 
    oggRatio: 0.05,
    oggQuality: 0.2,
  },
  medium: { 
    bitrate: 128, 
    label: 'Medium (128 kbps)', 
    mp3Ratio: 0.08, 
    oggRatio: 0.06,
    oggQuality: 0.4,
  },
  high: { 
    bitrate: 192, 
    label: 'High (192 kbps)', 
    mp3Ratio: 0.12, 
    oggRatio: 0.09,
    oggQuality: 0.6,
  },
  best: { 
    bitrate: 320, 
    label: 'Best (320 kbps)', 
    mp3Ratio: 0.20, 
    oggRatio: 0.15,
    oggQuality: 0.8,
  },
};

// Utility: Convert AudioBuffer to WAV ArrayBuffer
const audioBufferToWav = (buffer) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  
  const dataLength = buffer.length * blockAlign;
  const bufferLength = 44 + dataLength;
  
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);
  
  // WAV header
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);
  
  // Write interleaved audio data
  const channels = [];
  for (let i = 0; i < numChannels; i++) {
    channels.push(buffer.getChannelData(i));
  }
  
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]));
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
  }
  
  return arrayBuffer;
};

// Utility: Convert AudioBuffer to MP3 using lamejs
const audioBufferToMp3 = (audioBuffer, bitrate = 128) => {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const samples = audioBuffer.length;
  
  // Get channel data
  const leftChannel = audioBuffer.getChannelData(0);
  const rightChannel = numChannels > 1 ? audioBuffer.getChannelData(1) : leftChannel;
  
  // Convert float32 to int16
  const convertToInt16 = (floatArray) => {
    const int16Array = new Int16Array(floatArray.length);
    for (let i = 0; i < floatArray.length; i++) {
      const s = Math.max(-1, Math.min(1, floatArray[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return int16Array;
  };
  
  const leftInt16 = convertToInt16(leftChannel);
  const rightInt16 = convertToInt16(rightChannel);
  
  // Create MP3 encoder
  const mp3encoder = new lamejs.Mp3Encoder(numChannels, sampleRate, bitrate);
  const mp3Data = [];
  
  // Encode in chunks
  const chunkSize = 1152;
  for (let i = 0; i < samples; i += chunkSize) {
    const leftChunk = leftInt16.subarray(i, i + chunkSize);
    const rightChunk = rightInt16.subarray(i, i + chunkSize);
    
    let mp3buf;
    if (numChannels === 2) {
      mp3buf = mp3encoder.encodeBuffer(leftChunk, rightChunk);
    } else {
      mp3buf = mp3encoder.encodeBuffer(leftChunk);
    }
    
    if (mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }
  }
  
  // Flush remaining data
  const mp3buf = mp3encoder.flush();
  if (mp3buf.length > 0) {
    mp3Data.push(mp3buf);
  }
  
  // Combine all chunks into single array
  const totalLength = mp3Data.reduce((acc, buf) => acc + buf.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const buf of mp3Data) {
    result.set(buf, offset);
    offset += buf.length;
  }
  
  return result.buffer;
};

// Utility: Convert AudioBuffer to OGG Vorbis using wasm-media-encoders
const audioBufferToOgg = async (audioBuffer, quality = 0.4) => {
  // Dynamic import for wasm-media-encoders
  const { createOggEncoder } = await import('wasm-media-encoders');
  
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  
  // Create the OGG Vorbis encoder
  const encoder = await createOggEncoder();
  
  // Configure encoder - quality is 0-10 scale for vorbis, we convert from 0-1
  const vorbisQuality = Math.round(quality * 10); // Convert 0-1 to 0-10
  
  encoder.configure({
    sampleRate: sampleRate,
    channels: numChannels,
    vbrQuality: vorbisQuality,
  });
  
  // Prepare interleaved audio data
  // The encoder expects interleaved Float32Array data
  const samples = audioBuffer.length;
  const interleavedData = new Float32Array(samples * numChannels);
  
  for (let i = 0; i < samples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      interleavedData[i * numChannels + ch] = audioBuffer.getChannelData(ch)[i];
    }
  }
  
  // Encode the audio
  const encodedChunks = [];
  
  // Process in chunks to avoid memory issues
  const chunkSize = 4096 * numChannels;
  for (let offset = 0; offset < interleavedData.length; offset += chunkSize) {
    const chunk = interleavedData.subarray(offset, Math.min(offset + chunkSize, interleavedData.length));
    const encoded = encoder.encode(chunk);
    if (encoded && encoded.length > 0) {
      encodedChunks.push(encoded);
    }
  }
  
  // Finalize encoding
  const finalChunk = encoder.finalize();
  if (finalChunk && finalChunk.length > 0) {
    encodedChunks.push(finalChunk);
  }
  
  // Combine all chunks into a single Uint8Array
  const totalLength = encodedChunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let position = 0;
  for (const chunk of encodedChunks) {
    result.set(chunk, position);
    position += chunk.length;
  }
  
  return result.buffer;
};

// Utility: ArrayBuffer to Base64
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
};

// Utility: Base64 to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Utility: Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Utility: Estimate audio size in bytes (WAV)
const estimateAudioSize = (audioBuffer) => {
  if (!audioBuffer) return 0;
  // WAV: 44 bytes header + (samples * channels * 2 bytes per sample)
  return 44 + (audioBuffer.length * audioBuffer.numberOfChannels * 2);
};

// Utility: Estimate compressed audio size
const estimateCompressedSize = (audioBuffer, format, quality) => {
  if (!audioBuffer) return 0;
  const wavSize = estimateAudioSize(audioBuffer);
  
  if (format === 'wav') return wavSize;
  
  const qualitySettings = COMPRESSION_QUALITY[quality];
  if (format === 'mp3') {
    return Math.ceil(wavSize * (qualitySettings?.mp3Ratio || 0.1));
  } else if (format === 'ogg') {
    return Math.ceil(wavSize * (qualitySettings?.oggRatio || 0.08));
  }
  
  return wavSize;
};

// Utility: Generate waveform data from AudioBuffer
const generateWaveform = (buffer, samples = 200) => {
  if (!buffer) return [];
  const channelData = buffer.getChannelData(0);
  const blockSize = Math.floor(channelData.length / samples);
  const peaks = [];
  
  for (let i = 0; i < samples; i++) {
    const start = blockSize * i;
    let max = 0;
    for (let j = 0; j < blockSize; j++) {
      const value = Math.abs(channelData[start + j] || 0);
      if (value > max) max = value;
    }
    peaks.push(max);
  }
  
  return peaks;
};

// Track item in selection list
const TrackItem = ({ track, isSelected, onToggle, showAudioSize, audioFormat, quality }) => {
  const Icon = TRACK_ICONS[track.type] || TRACK_ICONS.default;
  const colorClass = TRACK_COLORS[track.type] || TRACK_COLORS.default;
  
  const wavSize = track.audioBuffer ? estimateAudioSize(track.audioBuffer) : 0;
  const compressedSize = track.audioBuffer ? estimateCompressedSize(track.audioBuffer, audioFormat, quality) : 0;
  const displaySize = compressedSize;
  const isCompressed = audioFormat !== 'wav';
  
  const FormatIcon = AUDIO_FORMATS[audioFormat]?.icon || Waves;
  const formatColor = AUDIO_FORMATS[audioFormat]?.color || 'text-cyan-400';
  
  return (
    <div
      onClick={onToggle}
      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'bg-violet-500/20 border border-violet-500/50' 
          : 'bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600'
      }`}
      data-testid={`batch-track-${track.id}`}
    >
      {/* Checkbox */}
      <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
        isSelected ? 'bg-violet-500 text-white' : 'bg-zinc-700 text-zinc-500'
      }`}>
        {isSelected ? <Check className="w-3 h-3" /> : null}
      </div>
      
      {/* Track icon */}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClass}`}>
        <Icon className="w-4 h-4" />
      </div>
      
      {/* Track info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white truncate">{track.name}</p>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>{track.type || 'audio'}</span>
          <span>•</span>
          <span>{track.midiClips?.length || 0} clips</span>
          <span>•</span>
          <span>{track.pluginChain?.length || 0} plugins</span>
          {showAudioSize && displaySize > 0 && (
            <>
              <span>•</span>
              <span className={`flex items-center gap-1 ${formatColor}`}>
                <FormatIcon className="w-3 h-3" />
                {formatFileSize(displaySize)}
                {isCompressed && wavSize > 0 && (
                  <span className="text-zinc-500">
                    ({Math.round((1 - compressedSize / wavSize) * 100)}% smaller)
                  </span>
                )}
              </span>
            </>
          )}
        </div>
      </div>
      
      {/* Track color indicator */}
      <div 
        className={`w-3 h-3 rounded-full ${track.color?.replace(/\/\d+/g, '') || 'bg-zinc-500'}`}
        style={{ backgroundColor: track.color?.includes('bg-') ? undefined : track.color }}
      />
    </div>
  );
};

// Format selector button
const FormatButton = ({ format, selected, onClick, disabled }) => {
  const config = AUDIO_FORMATS[format];
  const Icon = config.icon;
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all flex flex-col items-center gap-1 ${
        selected 
          ? `bg-zinc-800 border-2 border-violet-500 ${config.color}` 
          : 'bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      data-testid={`format-${format}`}
    >
      <Icon className="w-4 h-4" />
      <span>{config.label}</span>
    </button>
  );
};

// Main component
export const BatchTrackExporter = ({
  isOpen,
  onClose,
  tracks,
  onImportTracks,
  projectName = 'Untitled Project',
  audioContext, // Pass audioContext for decoding audio on import
}) => {
  // State
  const [mode, setMode] = useState('export'); // 'export' | 'import'
  const [selectedTrackIds, setSelectedTrackIds] = useState(new Set());
  const [importData, setImportData] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [includeAudio, setIncludeAudio] = useState(true);
  const [audioFormat, setAudioFormat] = useState('ogg'); // 'wav' | 'mp3' | 'ogg'
  const [compressionQuality, setCompressionQuality] = useState('medium');
  const [exportProgress, setExportProgress] = useState(0);
  const [importProgress, setImportProgress] = useState(0);
  const [currentTrackName, setCurrentTrackName] = useState('');
  const fileInputRef = useRef(null);

  // Calculate estimated export size
  const { estimatedSize, wavSize, tracksWithAudio } = useMemo(() => {
    if (mode !== 'export') return { estimatedSize: 0, wavSize: 0, tracksWithAudio: 0 };
    
    const selectedTracks = tracks.filter(t => selectedTrackIds.has(t.id));
    let baseSize = 1000; // Base JSON overhead
    let totalWavSize = 0;
    let totalCompressedSize = 0;
    let audioCount = 0;
    
    selectedTracks.forEach(track => {
      baseSize += 500; // Track metadata overhead
      baseSize += JSON.stringify(track.pluginChain || []).length;
      baseSize += JSON.stringify(track.midiClips || []).length;
      
      if (track.audioBuffer) {
        audioCount++;
        const wavBytes = estimateAudioSize(track.audioBuffer);
        const compBytes = estimateCompressedSize(track.audioBuffer, audioFormat, compressionQuality);
        totalWavSize += wavBytes;
        totalCompressedSize += compBytes;
      }
    });
    
    let totalSize = baseSize;
    if (includeAudio) {
      // Base64 encoding adds ~33% overhead
      totalSize += Math.ceil(totalCompressedSize * 1.37);
    }
    
    return {
      estimatedSize: totalSize,
      wavSize: Math.ceil(totalWavSize * 1.37) + baseSize,
      tracksWithAudio: audioCount,
    };
  }, [tracks, selectedTrackIds, includeAudio, audioFormat, compressionQuality, mode]);

  // Calculate savings percentage
  const savingsPercent = useMemo(() => {
    if (audioFormat === 'wav' || wavSize === 0) return 0;
    return Math.round((1 - estimatedSize / wavSize) * 100);
  }, [audioFormat, estimatedSize, wavSize]);

  // Toggle track selection
  const toggleTrack = useCallback((trackId) => {
    setSelectedTrackIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(trackId)) {
        newSet.delete(trackId);
      } else {
        newSet.add(trackId);
      }
      return newSet;
    });
  }, []);

  // Select all tracks
  const selectAll = useCallback(() => {
    const trackList = mode === 'export' ? tracks : (importData?.tracks || []);
    setSelectedTrackIds(new Set(trackList.map(t => t.id)));
  }, [tracks, importData, mode]);

  // Deselect all tracks
  const deselectAll = useCallback(() => {
    setSelectedTrackIds(new Set());
  }, []);

  // Export selected tracks
  const handleExport = useCallback(async () => {
    if (selectedTrackIds.size === 0) {
      toast.error('Select at least one track to export');
      return;
    }

    setIsProcessing(true);
    setExportProgress(0);
    setCurrentTrackName('');

    try {
      const selectedTracks = tracks.filter(t => selectedTrackIds.has(t.id));
      const totalTracks = selectedTracks.length;
      const qualitySettings = COMPRESSION_QUALITY[compressionQuality];
      const bitrate = qualitySettings?.bitrate || 128;
      const oggQuality = qualitySettings?.oggQuality || 0.4;
      
      const processedTracks = [];
      let audioTracksProcessed = 0;
      
      for (let i = 0; i < selectedTracks.length; i++) {
        const track = selectedTracks[i];
        setCurrentTrackName(track.name);
        setExportProgress(Math.round(((i + 0.2) / totalTracks) * 100));
        
        const trackData = {
          // Core properties
          id: track.id,
          name: track.name,
          type: track.type || 'audio',
          instrumentType: track.instrumentType,
          color: track.color,
          
          // Mixer settings
          volume: track.volume,
          pan: track.pan,
          muted: track.muted,
          solo: track.solo,
          
          // EQ and effects
          eq: track.eq,
          effects: track.effects,
          
          // Plugin chain (serialize settings)
          pluginChain: (track.pluginChain || []).map(plugin => ({
            id: plugin.id,
            type: plugin.type || plugin.name?.toLowerCase().replace(/\s+/g, '-'),
            name: plugin.name,
            enabled: plugin.enabled !== false,
            params: plugin.params || plugin.getState?.()?.parameters || {},
          })),
          
          // MIDI clips (for instrument/drum tracks)
          midiClips: (track.midiClips || []).map(clip => ({
            id: clip.id,
            name: clip.name,
            startTime: clip.startTime,
            duration: clip.duration,
            notes: clip.notes || [],
          })),
          
          // Audio region metadata
          startTime: track.startTime,
          duration: track.duration,
          hasAudio: !!track.audioBuffer,
          
          // Waveform data for preview (simplified)
          waveformData: track.waveformData?.length > 200 
            ? track.waveformData.filter((_, idx) => idx % Math.ceil(track.waveformData.length / 200) === 0)
            : track.waveformData,
        };
        
        // Include audio data if enabled and track has audio
        if (includeAudio && track.audioBuffer) {
          try {
            setExportProgress(Math.round(((i + 0.5) / totalTracks) * 100));
            
            let audioArrayBuffer;
            let format;
            
            if (audioFormat === 'ogg') {
              // Encode as OGG Vorbis
              audioArrayBuffer = await audioBufferToOgg(track.audioBuffer, oggQuality);
              format = 'ogg';
            } else if (audioFormat === 'mp3') {
              // Encode as MP3
              audioArrayBuffer = audioBufferToMp3(track.audioBuffer, bitrate);
              format = 'mp3';
            } else {
              // Encode as WAV
              audioArrayBuffer = audioBufferToWav(track.audioBuffer);
              format = 'wav';
            }
            
            const base64Audio = arrayBufferToBase64(audioArrayBuffer);
            trackData.audioData = {
              format: format,
              encoding: 'base64',
              sampleRate: track.audioBuffer.sampleRate,
              channels: track.audioBuffer.numberOfChannels,
              duration: track.audioBuffer.duration,
              bitrate: audioFormat === 'mp3' ? bitrate : null,
              quality: audioFormat === 'ogg' ? compressionQuality : null,
              originalSize: estimateAudioSize(track.audioBuffer),
              compressedSize: audioArrayBuffer.byteLength,
              data: base64Audio,
            };
            audioTracksProcessed++;
          } catch (audioErr) {
            console.error(`Failed to encode audio for track ${track.name}:`, audioErr);
            toast.warning(`Could not include audio for "${track.name}"`);
          }
        }
        
        processedTracks.push(trackData);
        setExportProgress(Math.round(((i + 1) / totalTracks) * 100));
        
        // Small delay to allow UI to update
        await new Promise(r => setTimeout(r, 10));
      }
      
      const exportData = {
        version: '2.2', // Version 2.2 supports OGG Vorbis
        type: 'track-pack',
        exportedAt: new Date().toISOString(),
        projectName: projectName,
        trackCount: processedTracks.length,
        includesAudio: includeAudio && tracksWithAudio > 0,
        audioFormat: audioFormat,
        compressionQuality: audioFormat !== 'wav' ? compressionQuality : null,
        tracks: processedTracks,
      };

      // Create and download file
      const jsonString = JSON.stringify(exportData);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const formatSuffix = audioFormat !== 'wav' ? `_${audioFormat}` : '_wav';
      const audioSuffix = includeAudio && tracksWithAudio > 0 ? formatSuffix : '';
      a.download = `${safeName}_${processedTracks.length}_tracks${audioSuffix}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const formatLabel = AUDIO_FORMATS[audioFormat]?.label || audioFormat.toUpperCase();
      const audioMsg = includeAudio && audioTracksProcessed > 0 
        ? ` (${audioTracksProcessed} with ${formatLabel} audio)` 
        : '';
      toast.success(`Exported ${processedTracks.length} track${processedTracks.length > 1 ? 's' : ''}${audioMsg}`);
      onClose?.();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export tracks');
    } finally {
      setIsProcessing(false);
      setExportProgress(0);
      setCurrentTrackName('');
    }
  }, [selectedTrackIds, tracks, projectName, includeAudio, audioFormat, compressionQuality, tracksWithAudio, onClose]);

  // Handle file selection for import
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFileName(file.name);
    setIsProcessing(true);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        
        // Validate structure
        if (data.type !== 'track-pack' || !Array.isArray(data.tracks)) {
          toast.error('Invalid file format: expected track-pack');
          setImportData(null);
          setIsProcessing(false);
          return;
        }
        
        setImportData(data);
        // Auto-select all tracks from the import file
        setSelectedTrackIds(new Set(data.tracks.map(t => t.id)));
        setIsProcessing(false);
      } catch (err) {
        console.error('Parse error:', err);
        toast.error('Failed to parse file. Make sure it\'s a valid track-pack JSON.');
        setImportData(null);
        setIsProcessing(false);
      }
    };
    reader.readAsText(file);
    
    e.target.value = '';
  }, []);

  // Import selected tracks
  const handleImport = useCallback(async () => {
    if (!importData || selectedTrackIds.size === 0) {
      toast.error('Select at least one track to import');
      return;
    }

    setIsProcessing(true);
    setImportProgress(0);
    setCurrentTrackName('');

    try {
      const selectedTracks = importData.tracks.filter(t => selectedTrackIds.has(t.id));
      const totalTracks = selectedTracks.length;
      const tracksToImport = [];
      let audioRestored = 0;
      
      for (let i = 0; i < selectedTracks.length; i++) {
        const track = selectedTracks[i];
        setCurrentTrackName(track.name);
        setImportProgress(Math.round(((i + 0.3) / totalTracks) * 100));
        
        const importedTrack = {
          ...track,
          id: `imported-${track.id}-${Date.now()}-${i}`,
          // Reset audio-related data initially
          audioBuffer: null,
          hasAudio: false,
          // Preserve MIDI data with new IDs
          midiClips: (track.midiClips || []).map((clip, idx) => ({
            ...clip,
            id: `imported-${clip.id}-${Date.now()}-${idx}`,
          })),
          // Preserve plugin chain with new IDs
          pluginChain: (track.pluginChain || []).map((plugin, idx) => ({
            ...plugin,
            id: `imported-${plugin.id}-${Date.now()}-${idx}`,
          })),
        };
        
        // Restore audio data if present
        if (track.audioData && track.audioData.data && audioContext) {
          try {
            setImportProgress(Math.round(((i + 0.6) / totalTracks) * 100));
            
            const arrayBuffer = base64ToArrayBuffer(track.audioData.data);
            
            // AudioContext can decode WAV, MP3, and OGG
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
            importedTrack.audioBuffer = audioBuffer;
            importedTrack.hasAudio = true;
            importedTrack.duration = audioBuffer.duration;
            importedTrack.waveformData = generateWaveform(audioBuffer);
            audioRestored++;
          } catch (audioErr) {
            console.error(`Failed to decode audio for track ${track.name}:`, audioErr);
            // Keep waveform data from export for visual reference
            importedTrack.waveformData = track.waveformData || [];
          }
        } else {
          // Use exported waveform data for visual reference
          importedTrack.waveformData = track.waveformData || [];
        }
        
        tracksToImport.push(importedTrack);
        setImportProgress(Math.round(((i + 1) / totalTracks) * 100));
        
        // Small delay to allow UI to update
        await new Promise(r => setTimeout(r, 10));
      }

      onImportTracks?.(tracksToImport);
      
      const importFormat = importData.audioFormat || 'wav';
      const formatLabel = AUDIO_FORMATS[importFormat]?.label || importFormat.toUpperCase();
      const audioMsg = audioRestored > 0 
        ? ` (${audioRestored} with ${formatLabel} audio restored)` 
        : importData.includesAudio && !audioContext
          ? ' (audio not restored - no audio context)'
          : '';
      toast.success(`Imported ${tracksToImport.length} track${tracksToImport.length > 1 ? 's' : ''}${audioMsg}`);
      onClose?.();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import tracks');
    } finally {
      setIsProcessing(false);
      setImportProgress(0);
      setCurrentTrackName('');
    }
  }, [importData, selectedTrackIds, onImportTracks, onClose, audioContext]);

  // Reset state when switching modes
  const switchMode = useCallback((newMode) => {
    setMode(newMode);
    setSelectedTrackIds(new Set());
    setImportData(null);
    setImportFileName('');
    setExportProgress(0);
    setImportProgress(0);
    setCurrentTrackName('');
  }, []);

  if (!isOpen) return null;

  const displayTracks = mode === 'export' ? tracks : (importData?.tracks || []);
  const allSelected = displayTracks.length > 0 && selectedTrackIds.size === displayTracks.length;
  const progress = mode === 'export' ? exportProgress : importProgress;
  
  // Get format config for footer
  const formatConfig = AUDIO_FORMATS[audioFormat];
  const FormatIcon = formatConfig?.icon || Waves;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && !isProcessing && onClose?.()}
        data-testid="batch-export-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Track Pack</h2>
                <p className="text-sm text-zinc-400">
                  {mode === 'export' ? 'Export with audio compression' : 'Import tracks from file'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="p-2 text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mode tabs */}
          <div className="p-3 border-b border-zinc-800">
            <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-1">
              <button
                onClick={() => switchMode('export')}
                disabled={isProcessing}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  mode === 'export' ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-white'
                } disabled:opacity-50`}
                data-testid="mode-export"
              >
                <FileDown className="w-4 h-4" />
                Export
              </button>
              <button
                onClick={() => switchMode('import')}
                disabled={isProcessing}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  mode === 'import' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'
                } disabled:opacity-50`}
                data-testid="mode-import"
              >
                <FileUp className="w-4 h-4" />
                Import
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {mode === 'import' && !importData ? (
              /* Import file selection */
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <div 
                  className="w-full p-8 border-2 border-dashed border-zinc-700 rounded-xl text-center hover:border-emerald-500/50 transition-colors cursor-pointer"
                  onClick={() => !isProcessing && fileInputRef.current?.click()}
                >
                  {isProcessing ? (
                    <Loader2 className="w-12 h-12 text-emerald-500 mx-auto mb-4 animate-spin" />
                  ) : (
                    <FileJson className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  )}
                  <p className="text-white font-medium mb-2">
                    {isProcessing ? 'Loading...' : 'Select Track Pack File'}
                  </p>
                  <p className="text-sm text-zinc-500">
                    Supports WAV, MP3, and OGG Vorbis audio
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="import-file-input"
                />
              </div>
            ) : (
              <>
                {/* Export options */}
                {mode === 'export' && (
                  <div className="p-3 border-b border-zinc-800 bg-zinc-800/30 space-y-3">
                    {/* Include Audio Toggle */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={includeAudio}
                          onCheckedChange={setIncludeAudio}
                          data-testid="include-audio-toggle"
                        />
                        <span className="text-sm text-white">Include Audio</span>
                        {tracksWithAudio > 0 && (
                          <span className="text-xs text-zinc-500">
                            ({tracksWithAudio} track{tracksWithAudio > 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-400 flex items-center gap-1">
                        <HardDrive className="w-3 h-3" />
                        ~{formatFileSize(estimatedSize)}
                      </div>
                    </div>
                    
                    {/* Format selector (only when audio is included and tracks have audio) */}
                    {includeAudio && tracksWithAudio > 0 && (
                      <>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-400">Audio Format</span>
                            {savingsPercent > 0 && (
                              <span className="text-xs text-emerald-400 font-medium">
                                ~{savingsPercent}% smaller than WAV
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <FormatButton 
                              format="wav" 
                              selected={audioFormat === 'wav'} 
                              onClick={() => setAudioFormat('wav')}
                              disabled={isProcessing}
                            />
                            <FormatButton 
                              format="mp3" 
                              selected={audioFormat === 'mp3'} 
                              onClick={() => setAudioFormat('mp3')}
                              disabled={isProcessing}
                            />
                            <FormatButton 
                              format="ogg" 
                              selected={audioFormat === 'ogg'} 
                              onClick={() => setAudioFormat('ogg')}
                              disabled={isProcessing}
                            />
                          </div>
                        </div>
                        
                        {/* Quality selector (for compressed formats) */}
                        {audioFormat !== 'wav' && (
                          <div className="flex items-center gap-2">
                            <Settings2 className="w-3.5 h-3.5 text-zinc-500" />
                            <span className="text-xs text-zinc-500">Quality:</span>
                            <select
                              value={compressionQuality}
                              onChange={(e) => setCompressionQuality(e.target.value)}
                              disabled={isProcessing}
                              className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-violet-500 disabled:opacity-50"
                              data-testid="compression-quality-select"
                            >
                              {Object.entries(COMPRESSION_QUALITY).map(([key, { label }]) => (
                                <option key={key} value={key}>{label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </>
                    )}
                    
                    {!includeAudio && (
                      <p className="text-xs text-zinc-500 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Only track settings, plugins, and MIDI will be exported
                      </p>
                    )}
                  </div>
                )}

                {/* Import info */}
                {mode === 'import' && importData && (
                  <div className="p-3 border-b border-zinc-800 bg-zinc-800/30">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-400 truncate flex-1 mr-2">{importFileName}</span>
                      <div className="flex items-center gap-3 text-zinc-400">
                        {importData.includesAudio && (
                          <span className={`flex items-center gap-1 ${AUDIO_FORMATS[importData.audioFormat]?.color || 'text-cyan-400'}`}>
                            {React.createElement(AUDIO_FORMATS[importData.audioFormat]?.icon || Waves, { className: 'w-3 h-3' })}
                            {importData.audioFormat?.toUpperCase() || 'WAV'}
                          </span>
                        )}
                        <span>v{importData.version || '1.0'}</span>
                      </div>
                    </div>
                    {importData.projectName && (
                      <p className="text-xs text-zinc-500 mt-1">From: {importData.projectName}</p>
                    )}
                    {importData.compressionQuality && (
                      <p className="text-xs text-zinc-500 mt-1">
                        Quality: {COMPRESSION_QUALITY[importData.compressionQuality]?.label || importData.compressionQuality}
                      </p>
                    )}
                  </div>
                )}

                {/* Selection header */}
                <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
                  <div className="text-sm text-zinc-400">
                    {selectedTrackIds.size} of {displayTracks.length} track{displayTracks.length !== 1 ? 's' : ''} selected
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={allSelected ? deselectAll : selectAll}
                      disabled={isProcessing}
                      className="text-xs px-2 py-1 rounded text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors flex items-center gap-1 disabled:opacity-50"
                      data-testid="toggle-all-tracks"
                    >
                      {allSelected ? (
                        <>
                          <Square className="w-3 h-3" />
                          Deselect All
                        </>
                      ) : (
                        <>
                          <CheckSquare className="w-3 h-3" />
                          Select All
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Track list */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {displayTracks.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">
                      <Music className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No tracks available</p>
                    </div>
                  ) : (
                    displayTracks.map(track => (
                      <TrackItem
                        key={track.id}
                        track={track}
                        isSelected={selectedTrackIds.has(track.id)}
                        onToggle={() => !isProcessing && toggleTrack(track.id)}
                        showAudioSize={mode === 'export' && includeAudio}
                        audioFormat={audioFormat}
                        quality={compressionQuality}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Progress bar */}
          {isProcessing && progress > 0 && (
            <div className="px-4 pb-2">
              <Progress value={progress} className="h-1" />
              <p className="text-xs text-zinc-500 text-center mt-1">
                {mode === 'export' ? 'Encoding' : 'Decoding'} {currentTrackName ? `"${currentTrackName}"` : ''}... {progress}%
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
            {mode === 'export' && (
              <div className="text-xs flex items-center gap-1">
                {!includeAudio ? (
                  <span className="text-zinc-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    No audio
                  </span>
                ) : tracksWithAudio > 0 ? (
                  <span className={`flex items-center gap-1 ${formatConfig?.color || 'text-cyan-400'}`}>
                    <FormatIcon className="w-3 h-3" />
                    {formatConfig?.label || audioFormat.toUpperCase()}
                  </span>
                ) : (
                  <span className="text-zinc-500">No audio tracks</span>
                )}
              </div>
            )}
            {mode === 'import' && importData && (
              <div className="text-xs flex items-center gap-1">
                {importData.includesAudio ? (
                  <span className={`flex items-center gap-1 ${AUDIO_FORMATS[importData.audioFormat]?.color || 'text-cyan-400'}`}>
                    {React.createElement(AUDIO_FORMATS[importData.audioFormat]?.icon || Waves, { className: 'w-3 h-3' })}
                    {AUDIO_FORMATS[importData.audioFormat]?.label || importData.audioFormat?.toUpperCase()} restoration
                  </span>
                ) : (
                  <span className="text-zinc-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    No audio in file
                  </span>
                )}
              </div>
            )}
            
            <div className="flex gap-2 ml-auto">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={isProcessing}
                className="text-zinc-400"
              >
                Cancel
              </Button>
              
              {mode === 'export' ? (
                <Button
                  onClick={handleExport}
                  disabled={selectedTrackIds.size === 0 || isProcessing}
                  className="bg-violet-600 hover:bg-violet-500"
                  data-testid="export-tracks-btn"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export {selectedTrackIds.size > 0 ? `(${selectedTrackIds.size})` : ''}
                </Button>
              ) : (
                <Button
                  onClick={handleImport}
                  disabled={!importData || selectedTrackIds.size === 0 || isProcessing}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="import-tracks-btn"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Import {selectedTrackIds.size > 0 ? `(${selectedTrackIds.size})` : ''}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BatchTrackExporter;
